import StencilHTMLElement from "./stencil-element";

/**
 * ProductCtlCard component for representing a single complementary product.
 * 
 * @see {@link file://./product-ctl.js}
 * @see {@link file://./../../../snippets/product-ctl-card.liquid}
 * @see {@link file://./../../../snippets/product-ctl.liquid}
 */
class ProductCtlCard extends StencilHTMLElement {
  name = "ProductCtlCard";
  busy = false;

  /** @type {HTMLButtonElement|null} */
  addButton = null;

  /** @type {NodeListOf<HTMLElement>} */
  optionValueButtons;

  /** @type {string|null} */
  parentProductId = null;

  /** @type {string|null} */
  selectedVariantId = null;

  /** Selection controls & UI refs */
  selectionToggle = null; // include checkbox (if present)
  togglerBar = null;      // the top include bar
  detailArea = null;      // wrapper for options area
  blocker = null;         // transparent overlay inside detailArea

  isSelected = false;
  isRequired = false;

  constructor() {
    super();
  }

  connectedCallback() {
    // Cache DOM + attrs
    this.addButton = this.querySelector(".btn-add");
    this.optionValueButtons = this.querySelectorAll(".product-ctl-option-value");

    this.selectionToggle = this.querySelector(".product-ctl-selection-toggle");
    this.togglerBar = this.querySelector(".product-ctl-card-toggler");
    this.detailArea = this.querySelector(".product-ctl-card-detail") || this;
    this.blocker = this.detailArea.querySelector(".ctl-blocker");

    this.parentProductId = this.getAttribute("parent") || null;
    this.selectedVariantId = this.getAttribute("first-available-variant") || null;

    // Required cards are always selected; optional start unselected
    this.isRequired = this.getAttribute("is-required") === "true";
    this.isSelected = this.isRequired || false;
    this.#syncSelectedStateClass();

    // A11y for hidden per-card Add button (kept for parity)
    if (this.addButton) {
      this.addButton.setAttribute("aria-live", "polite");
      this.addButton.setAttribute("aria-busy", "false");
      this.addButton.addEventListener("click", (e) => this.#onAddClick(e));
    }

    // Toggle include
    this.selectionToggle?.addEventListener("click", () => this.#onSelectionToggleClick());

    // Radios/selects: enable only when selected
    this.optionValueButtons.forEach((el) => {
      if (!this.isRequired) {
        el.disabled = !this.isSelected || el.dataset.available === "false";
      }
      el.addEventListener("change", (e) => this.#onOptionChange(e));
    });

    // Blocker overlay: any interaction while unselected nudges the include bar
    const intercept = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.#nudgeIncludeBar();
    };
    this.blocker?.addEventListener("click", intercept);
    this.blocker?.addEventListener("pointerdown", intercept);

    // console.log(this.logPrefix, "Connected");
  }

  #onSelectionToggleClick() {
    this.isSelected = !this.isSelected;
    this.#syncSelectedStateClass();

    // Enable/disable radios/selects based on selection and availability
    this.optionValueButtons.forEach((el) => {
      el.disabled = !this.isSelected || el.dataset.available === "false";
    });

    document.dispatchEvent(new CustomEvent("ctl-card:selection-toggle", { detail: {} }));
  }

  #onOptionChange(e) {
    const el = /** @type {HTMLElement} */ (e.target);
    const vid = el?.dataset?.variant || null;
    if (!vid) return;

    this.selectedVariantId = vid;
    console.log(this.logPrefix, "Option value changed:", this.selectedVariantId);

    // Notify local listeners (e.g., swap image/price)
    this.dispatchEvent(new CustomEvent("ctl:variant-change", {
      bubbles: true,
      detail: { variantId: this.selectedVariantId }
    }));
  }

  async #onAddClick(e) {
    e.preventDefault();
    if (this.busy) return;

    this.busy = true;
    this.startLoading();

    const cartPayload = [{
      id: this.selectedVariantId, // guaranteed by your constraints
      quantity: 1,
      properties: {
        _ctl_addition: true,
        _ctl_parent: this.parentProductId ?? ""
      }
    }];

    console.log(this.logPrefix, "Cart payload:", cartPayload);

    try {
      await window.MP.Extensions.Cart.add(cartPayload);
      this.#announce("Added!");
      // Success/error events handled by Cart extension
    } catch (err) {
      console.error(this.logPrefix, "Add to cart failed:", err);
      this.#announce("Couldn’t add to cart. Please try again.");
    } finally {
      this.busy = false;
      this.stopLoading();
    }
  }

  /** Visual nudge to the include bar */
  #nudgeIncludeBar() {
    // Restart wiggle animation
    this.togglerBar?.classList.remove("is-wiggling");
    // force reflow to retrigger animation reliably
    // eslint-disable-next-line no-unused-expressions
    this.togglerBar && this.togglerBar.offsetHeight;
    this.togglerBar?.classList.add("is-wiggling");

    // Bring the toggle into view and focus it for quick action
    this.togglerBar?.scrollIntoView({ block: "center", behavior: "smooth" });
    this.selectionToggle?.focus({ preventScroll: true });

    // Gentle announcement for AT users
    this.#announce("Select ‘Include in bundle’ to choose a size.");
  }

  #syncSelectedStateClass() {
    // Drives the blocker visibility via CSS: .is-unselected .ctl-blocker { display:block; }
    this.classList.toggle("is-unselected", !this.isSelected && !this.isRequired);
  }

  // Public: used internally and by the container component
  startLoading() {
    if (!this.addButton) return;
    this.addButton.querySelector(".spinner-overlay")?.classList.remove("d-none");
    this.addButton.querySelector("span")?.classList.add("opacity-0");
    this.addButton.disabled = true;
    this.addButton.setAttribute("aria-busy", "true");
  }

  stopLoading() {
    if (!this.addButton) return;
    this.addButton.querySelector(".spinner-overlay")?.classList.add("d-none");
    this.addButton.querySelector("span")?.classList.remove("opacity-0");
    this.addButton.disabled = false;
    this.addButton.setAttribute("aria-busy", "false");
  }

  #announce(text) {
    if (!this.addButton) return;
    this.addButton.setAttribute("aria-label", text);
  }
}

export default ProductCtlCard;
